import {
  ListBulletIcon,
  CodeBracketIcon,
  FaceSmileIcon,
  MicrophoneIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { BsKanban } from 'react-icons/bs';
import { FaStickyNote } from 'react-icons/fa';
import { GiTomato, GiCalendar } from 'react-icons/gi';
import { SiGooglecalendar } from 'react-icons/si';

import { AIAssistant } from '@/apps/AIAssistant';
import Calendar from '@/apps/Calendar';
import Checklist from '@/apps/Checklist';
import ChecklistConfig from '@/apps/Checklist/components/ChecklistConfig';
import DailyMood from '@/apps/DailyMood';
import Embed from '@/apps/Embed';
import Fireflies, { FirefliesConfig } from '@/apps/Fireflies';
import Kanban from '@/apps/Kanban';
import Notes from '@/apps/Notes';
import Pomodoro from '@/apps/Pomodoro';
import ModeControls from '@/apps/Pomodoro/components/ModeControls';
import Schedule from '@/apps/Schedule';
import ScheduleConfig from '@/apps/Schedule/components/ScheduleConfig';
import TeamsSchedule from '@/apps/TeamsSchedule';
import TeamsConfig from '@/apps/TeamsSchedule/components/TeamsConfig';

export const LINK_TYPES = {
  SCHEDULE_EVENT: 'schedule_event',
  NOTE: 'note',
  KANBAN_CARD: 'kanban_card',
  CHECKLIST_ITEM: 'checklist_item',
  FIREFLIES_TRANSCRIPT: 'fireflies_transcript',
} as const;

export const WIDGET_CATEGORIES = {
  PRODUCTIVITY: 'productivity',
  PLANNING: 'planning',
  MEDIA: 'media',
} as const;

export const FREE_WIDGET_IDS = ['notes', 'calendar', 'checklist', 'schedule'] as const;
export const MAX_FREE_WIDGETS = 3;

export const AVAILABLE_WIDGETS = [
  {
    id: 'notes',
    title: 'Notes',
    description:
      'Quick access to your notes and thoughts. Jot down ideas, reminders, or important information instantly.',
    component: Notes,
    icon: FaStickyNote,
    category: WIDGET_CATEGORIES.PRODUCTIVITY,
    recoverable: true,
    allowMultiple: false,
    adjustable: false,
    premium: false,
  },
  {
    id: 'schedule',
    title: 'Google Calendar',
    description:
      'View your Google Calendar events directly from your dashboard. Stay on top of your schedule.',
    component: Schedule,
    configComponent: ScheduleConfig,
    icon: SiGooglecalendar,
    category: WIDGET_CATEGORIES.PLANNING,
    allowMultiple: false,
    recoverable: false,
    adjustable: false,
    premium: false,
  },
  {
    id: 'kanban',
    title: 'Kanban',
    description:
      'Organize your tasks with a visual Kanban board. Drag and drop cards to manage your workflow efficiently.',
    component: Kanban,
    icon: BsKanban,
    category: WIDGET_CATEGORIES.PLANNING,
    allowMultiple: false,
    recoverable: true,
    adjustable: false,
    premium: true,
  },
  // We need a CASA certification to have this scope approved on gcloud
  // {
  //   id: 'email',
  //   title: 'Gmail',
  //   description: 'Access your Gmail inbox without switching tabs.',
  //   component: Email,
  //   icon: SiGmail,
  //   category: WIDGET_CATEGORIES.PRODUCTIVITY,
  //   allowMultiple: false,
  //   recoverable: false,
  //   adjustable: false
  // },
  // Spotify restricted a lot of scopes, so we're not using it anymore
  // {
  //   id: 'spotify',
  //   title: 'Spotify',
  //   description:
  //     'Control your Spotify playback and manage your playlists without leaving your workspace.',
  //   component: Spotify,
  //   icon: FaSpotify,
  //   category: WIDGET_CATEGORIES.MEDIA,
  //   allowMultiple: false,
  //   recoverable: false,
  //   adjustable: false,
  //   premium: true,
  // },
  {
    id: 'checklist',
    title: 'Checklist',
    description:
      'Create and manage simple to-do lists. Track your daily tasks and mark them complete as you go.',
    component: Checklist,
    configComponent: ChecklistConfig,
    icon: ListBulletIcon,
    category: WIDGET_CATEGORIES.PRODUCTIVITY,
    allowMultiple: false,
    recoverable: true,
    adjustable: false,
    premium: false,
  },
  {
    id: 'pomodoro',
    title: 'Pomodoro',
    description:
      'Stay focused with the Pomodoro technique. Time your work sessions and breaks for maximum productivity.',
    component: Pomodoro,
    configComponent: ModeControls,
    icon: GiTomato,
    category: WIDGET_CATEGORIES.PRODUCTIVITY,
    allowMultiple: false,
    recoverable: false,
    adjustable: true,
    premium: true,
  },
  {
    id: 'teams-schedule',
    title: 'Teams Calendar',
    description:
      'Keep track of your Microsoft Teams meetings and events in one convenient location.',
    component: TeamsSchedule,
    configComponent: TeamsConfig,
    icon: GiCalendar,
    category: WIDGET_CATEGORIES.PLANNING,
    recoverable: false,
    allowMultiple: false,
    adjustable: false,
    premium: true,
  },
  {
    id: 'embed',
    title: 'Embed',
    description:
      'Embed any website or web application directly in your dashboard for quick access.',
    component: Embed,
    icon: CodeBracketIcon,
    category: WIDGET_CATEGORIES.MEDIA,
    recoverable: false,
    allowMultiple: true,
    adjustable: true,
    premium: true,
  },
  {
    id: 'daily-mood',
    title: 'Daily Mood',
    description:
      'Track your daily mood and emotional well-being with simple mood logging and tracking.',
    component: DailyMood,
    icon: FaceSmileIcon,
    category: WIDGET_CATEGORIES.PRODUCTIVITY,
    allowMultiple: false,
    recoverable: true,
    adjustable: false,
    premium: true,
  },
  {
    id: 'calendar',
    title: 'Calendar',
    description: 'Track important dates and upcoming events with a simple calendar view.',
    component: Calendar,
    icon: GiCalendar,
    category: WIDGET_CATEGORIES.PLANNING,
    allowMultiple: false,
    recoverable: true,
    adjustable: false,
    premium: false,
  },
  {
    id: 'ai-assistant',
    title: 'AI Assistant',
    description:
      'Smart AI assistant that provides personalized recommendations based on your schedule and tasks.',
    component: AIAssistant,
    icon: SparklesIcon,
    category: WIDGET_CATEGORIES.PRODUCTIVITY,
    allowMultiple: false,
    recoverable: true,
    adjustable: false,
    premium: true,
  },
  {
    id: 'fireflies',
    title: 'Fireflies',
    description:
      'View recent meeting transcripts from Fireflies. See summaries, action items, and link to full transcripts.',
    component: Fireflies,
    configComponent: FirefliesConfig,
    icon: MicrophoneIcon,
    category: WIDGET_CATEGORIES.PRODUCTIVITY,
    allowMultiple: false,
    recoverable: true,
    adjustable: false,
    premium: true,
  },
] as const;
